import { IconInfo, IconTrash } from '@posthog/icons'
import { LemonSwitch } from '@posthog/lemon-ui'
import { useActions, useValues } from 'kea'
import { CurrencyDropdown } from 'lib/components/BaseCurrency/CurrencyDropdown'
import { TaxonomicFilterGroupType } from 'lib/components/TaxonomicFilter/types'
import { TaxonomicPopover } from 'lib/components/TaxonomicPopover/TaxonomicPopover'
import { LemonButton } from 'lib/lemon-ui/LemonButton'
import { LemonTable } from 'lib/lemon-ui/LemonTable'
import { Tooltip } from 'lib/lemon-ui/Tooltip'

import { RevenueAnalyticsEventItem } from '~/queries/schema/schema-general'

import { revenueAnalyticsSettingsLogic } from './revenueAnalyticsSettingsLogic'

export function EventConfiguration({ buttonRef }: { buttonRef?: React.RefObject<HTMLButtonElement> }): JSX.Element {
    const { events, saveEventsDisabledReason, changesMadeToEvents } = useValues(revenueAnalyticsSettingsLogic)
    const {
        addEvent,
        deleteEvent,
        updateEventRevenueProperty,
        updateEventRevenueCurrencyProperty,
        updateEventCurrencyAwareDecimalProperty,
        save,
    } = useActions(revenueAnalyticsSettingsLogic)

    return (
        <div>
            <h3 className="mb-2">Event Configuration</h3>
            <p className="mb-4">
                PostHog can display revenue data in our Revenue Analytics product from any event. You can configure as
                many events as you want, and specify the revenue property and currency for each event individually.
            </p>
            <LemonTable<RevenueAnalyticsEventItem>
                columns={[
                    { key: 'eventName', title: 'Event name', dataIndex: 'eventName' },
                    {
                        key: 'revenueProperty',
                        title: 'Revenue property',
                        dataIndex: 'revenueProperty',
                        render: (_, item: RevenueAnalyticsEventItem) => {
                            return (
                                <TaxonomicPopover
                                    showNumericalPropsOnly
                                    size="small"
                                    className="my-1"
                                    groupType={TaxonomicFilterGroupType.EventProperties}
                                    onChange={(newPropertyName) =>
                                        updateEventRevenueProperty(item.eventName, newPropertyName)
                                    }
                                    value={item.revenueProperty}
                                    placeholder="Choose event property"
                                    disabledReason={
                                        item.eventName === '$pageview' || item.eventName === '$autocapture'
                                            ? 'Built-in events must use revenue'
                                            : undefined
                                    }
                                />
                            )
                        },
                    },
                    {
                        key: 'revenueCurrencyProperty',
                        title: (
                            <span>
                                Revenue currency property
                                <Tooltip title="The currency of the revenue event. You can choose between a property on your event OR a hardcoded currency.">
                                    <IconInfo className="ml-1" />
                                </Tooltip>
                            </span>
                        ),
                        dataIndex: 'revenueCurrencyProperty',
                        render: (_, item: RevenueAnalyticsEventItem) => {
                            return (
                                <div className="flex flex-col w-full gap-3 my-1 min-w-[250px] whitespace-nowrap">
                                    <div className="flex flex-row gap-1">
                                        <span className="font-bold">Dynamic property: </span>
                                        <TaxonomicPopover
                                            size="small"
                                            groupType={TaxonomicFilterGroupType.EventProperties}
                                            onChange={(newPropertyName) =>
                                                updateEventRevenueCurrencyProperty(item.eventName, {
                                                    property: newPropertyName,
                                                })
                                            }
                                            value={item.revenueCurrencyProperty.property ?? null}
                                            placeholder="Choose event property"
                                        />
                                    </div>
                                    <div className="flex flex-row gap-1">
                                        or <span className="font-bold">Static currency: </span>
                                        <CurrencyDropdown
                                            size="small"
                                            onChange={(currency) =>
                                                updateEventRevenueCurrencyProperty(item.eventName, {
                                                    static: currency!,
                                                })
                                            }
                                            value={item.revenueCurrencyProperty.static ?? null}
                                        />
                                    </div>
                                </div>
                            )
                        },
                    },
                    {
                        key: 'currencyAwareDecimal',
                        title: (
                            <span>
                                In currency's smallest unit?
                                <Tooltip title="Whether you are sending revenue in the smallest unit of currency (e.g. cents for USD, yen for JPY). If you are, we will divide the revenue by the smallest unit of currency (e.g. 100 for USD, 1 for JPY) when parsing the revenue.">
                                    <IconInfo className="ml-1" />
                                </Tooltip>
                            </span>
                        ),
                        dataIndex: 'currencyAwareDecimal',
                        render: (_, item: RevenueAnalyticsEventItem) => {
                            return (
                                <LemonSwitch
                                    checked={item.currencyAwareDecimal}
                                    onChange={(checked) =>
                                        updateEventCurrencyAwareDecimalProperty(item.eventName, checked)
                                    }
                                />
                            )
                        },
                    },
                    {
                        key: 'delete',
                        fullWidth: true,
                        title: (
                            <div className="flex flex-col gap-1 items-end w-full">
                                <div className="flex flex-row w-full gap-1 justify-end my-2">
                                    <TaxonomicPopover
                                        type="primary"
                                        groupType={TaxonomicFilterGroupType.CustomEvents}
                                        onChange={addEvent}
                                        value={undefined}
                                        placeholder="Create revenue event"
                                        placeholderClass=""
                                        excludedProperties={{
                                            [TaxonomicFilterGroupType.CustomEvents]: [
                                                null,
                                                ...events.map((item) => item.eventName),
                                            ],
                                        }}
                                        id="data-management-revenue-settings-add-event"
                                        ref={buttonRef}
                                    />

                                    <LemonButton
                                        type="primary"
                                        onClick={save}
                                        disabledReason={saveEventsDisabledReason}
                                    >
                                        Save
                                    </LemonButton>
                                </div>
                                {changesMadeToEvents && (
                                    <span className="text-xs text-error normal-case font-normal">
                                        Remember to save your changes to take effect
                                    </span>
                                )}
                            </div>
                        ),
                        render: (_, item) => (
                            <LemonButton
                                className="float-right"
                                size="small"
                                type="secondary"
                                onClick={() => deleteEvent(item.eventName)}
                                icon={<IconTrash />}
                            >
                                Delete
                            </LemonButton>
                        ),
                    },
                ]}
                dataSource={events}
                rowKey={(item) => item.eventName}
            />
        </div>
    )
}
